import React, { useContext, useEffect, useState } from "react";
import LoadingScreen from "../loading-screen/LoadingScreen";
import { doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";
import { auth, db } from "../../config/firebase";
import { Link, useNavigate } from "react-router-dom";
import { AppContext } from "../../context/AppContext";
import { getStripeSession, mintLicense } from "../../utils/functions";
import { LicenseRequestType } from "../../utils/enums/LicenseRequestType";
import MintingLoadingScreen from "../lottie-animations/MintingLoadingScreen";
import AnimationSuccess from "../lottie-animations/AnimationSuccess";
import { Button, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { IconChevronRight } from "@tabler/icons-react";
import AnimationFailed from "../lottie-animations/AnimationFailed";

export const MintLicense = ({ nftUid, licenseUid, publicAddress, sessionId, licenseRequestUid }) => {
  const navigate = useNavigate();
  const { state: { userDetails } } = useContext(AppContext);
  
  const [status, setStatus] = useState("loading");
  const [licenseRequest, setLicenseRequest] = useState(null);
  const [txnHash, setTxnHash] = useState('');

  // Load license request data
  useEffect(() => {
    if (licenseRequestUid) {
      console.log("DEBUG: Setting up listener for license request:", licenseRequestUid);
      const unsubscribe = onSnapshot(doc(db, "license_requests", licenseRequestUid), (docSnap) => {
        if (!docSnap.exists()) {
          console.log("DEBUG: License request document doesn't exist");
          // navigate("/");
          return;
        }
        
        const data = docSnap.data();
        console.log("DEBUG: License request data received:", data);
        
        if (!data || !data.uid) {
          console.log("DEBUG: License request data is invalid");
          // navigate("/");
          return;
        }
        
        console.log("DEBUG: Setting license request state with data:", data);
        setLicenseRequest(data);
        console.log(data);
      }, (error) => {
        console.error("DEBUG: Error in license request listener:", error);
      });

      return () => {
        console.log("DEBUG: Cleaning up license request listener");
        unsubscribe();
      };
    }
  }, [licenseRequestUid]);

  // Check session status and mint license
  useEffect(() => {
    async function processLicenseMinting() {
      console.log("DEBUG: processLicenseMinting triggered with:", {
        hasUserDetails: userDetails,
        hasLicenseRequest: licenseRequest,
        licenseUid: licenseUid,
        currentState: licenseRequest?.state,
        hasSessionId: sessionId,
        buyerUid: auth.currentUser.uid
      });

      if (licenseRequest.state === LicenseRequestType.MINTED ) {
          console.log("DEBUG: License request is already minted or paid, exiting early");
          setStatus("success");
          return;
      }

      if (!userDetails || !licenseRequest || !licenseRequest?.licenseUid|| !sessionId) {
        console.log("DEBUG: Exiting early due to missing requirements");
        
        return;
      }

      try {
        console.log("DEBUG: Starting license minting process");
        setStatus("verifying");
        
        // Verify payment status
        console.log("DEBUG: Verifying payment status for session:", sessionId);
        const sessionStatus = await getStripeSession({ sessionId });
        console.log("DEBUG: Session status response:", sessionStatus);
        
        if (sessionStatus.data !== "complete") {
          console.log("DEBUG: Payment not complete, updating license request as failed");
          const requestRef = doc(db, 'license_requests', licenseRequest?.uid);
          await updateDoc(requestRef, { 
            paid: false, 
            txnHash: '', 
            state: LicenseRequestType.FAILED 
          });
          setStatus("failed");
          return;
        }
        
        console.log("DEBUG: Payment verified as complete, proceeding to minting");
        setStatus("minting");
        
        // Prepare license data
        const licenseUid = licenseRequest.licenseUid;
        // Check if license already exists for this request
        console.log("DEBUG: Checking for existing license with requestUid:", licenseUid);
        const licenseDoc = await getDoc(doc(db, "licenses", licenseRequest?.licenseUid));
        const licenseData = licenseDoc.data();
        
        const txn = await mintLicense(
          publicAddress,
          licenseData.nftUid,
          parseInt(licenseData.termsOfYears || 0) * 365,
          licenseData?.uid,
          licenseRequest.uid,
          auth.currentUser.uid,
          sessionId
        );
        setLicenseRequest(prev => ({...prev, txnHash: txn?.transaction_hash || null}))
        setTxnHash( txn?.transaction_hash || null)
        const finalStatus = "success";
        console.log("DEBUG: Setting final status to:", finalStatus);
        setStatus(finalStatus);
        return;
      } catch (error) {
        console.error("DEBUG: Error in license minting process:", error);
        const requestRef = doc(db, 'license_requests', licenseRequest.uid);
        await updateDoc(requestRef, { state: LicenseRequestType.FAILED });
        setStatus("failed");
      }
    }
    if (licenseRequest && sessionId) {

      console.log("DEBUG: useEffect dependencies changed:", {
        hasLicenseRequest: !!licenseRequest,
        hasUserDetails: !!userDetails,
        requestUid: licenseRequest?.uid,
        publicAddress,
        sessionId,
        buyerUid: auth.currentUser.uid
      });
      processLicenseMinting();
    }
  }, [licenseRequest, sessionId]);

  // Status-based messages
  const statusMessages = {
    "loading": "Loading license information...",
    "verifying": "Verifying payment status...",
    "minting": "Minting your license on blockchain...",
    "paid": "Payment was successful, but minting is pending",
    "success": "Your license was minted successfully!",
    "failed": "License minting failed"
  };

  return (
    <>
      {status === "loading" && <LoadingScreen />}
      
      {(status === "verifying" || status === "minting" || status === "paid") && (
        <MintingLoadingScreen message={statusMessages[status]} />
      )}
      
      {(licenseRequest?.txnHash || (status === "success" && txnHash)) && (
        <Stack direction={"column"} alignItems={"center"} gap={2} maxWidth={"sm"}
          justifyContent={'center'} alignContent={'center'}
          sx={{ m: "auto", borderRadius: 5, boxShadow: 5, bgcolor: "#FFF", p: 5 }}>
          <AnimationSuccess open />
          <Typography variant={"h3"}>
            License has been minted!
          </Typography>
          <Typography sx={{ color: grey[600] }}>View your NFT on the blockchain at</Typography>
          <Typography>
            <a href={"https://polygonscan.com/tx/" + (licenseRequest?.txnHash || txnHash)} target={'_blank'} rel="noopener noreferrer">
              Polygonscan.com
            </a>
          </Typography>
          <Typography sx={{ color: grey[600] }}>
            By timestamping your work as an NFT, you've taken a proactive step to safeguard your creative
            work, helping to protect against copyright infringement in the future. 🔥💪
          </Typography>

          <Button
            component={Link}
            to={"/portfolio"}
            className={"sign-button"}
            endIcon={<IconChevronRight />}
            sx={{ textTransform: "none", px: 2, borderRadius: 3 }}
          >
            View this NFT in your portfolio
          </Button>
        </Stack>
      )}
      
      {status === "failed" && (
        <Stack direction={"column"} alignItems={"center"} gap={2} maxWidth={"sm"}
          justifyContent={'center'} alignContent={'center'}
          sx={{ m: "auto", borderRadius: 5, boxShadow: 5, bgcolor: "#FFF", p: 5, height: '100%' }}>
          <AnimationFailed open />

          <Typography variant={"h3"}>
            License has failed to mint!
          </Typography>

          <Button
            component={Link}
            to={"/portfolio"}
            className={"sign-button"}
            endIcon={<IconChevronRight />} size={"large"}
            sx={{ textTransform: "none", px: 3, borderRadius: 3 }}
          >
            View this work in your portfolio and retry minting
          </Button>
        </Stack>
      )}
    </>
  );
};